const STORAGE_PREFIX = 'offline-cache';
const STORAGE_VERSION = 1;

interface StoredSnapshot<T> {
  version: number;
  cachedAt: number;
  data: T;
  dataVersion?: string | number;
}

export interface OfflineSnapshot<T> {
  cachedAt: number;
  data: T;
  dataVersion?: string | number;
}

const isBrowser = typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

function buildKey(namespace: string, tenantSlug: string): string {
  const tenant = tenantSlug || 'default';
  return `${STORAGE_PREFIX}:${namespace}:${tenant}`;
}

export function saveOfflineSnapshot<T>(
  namespace: string,
  tenantSlug: string,
  data: T,
  dataVersion?: string | number,
): void {
  if (!isBrowser) {
    return;
  }
  const key = buildKey(namespace, tenantSlug);
  const payload: StoredSnapshot<T> = {
    version: STORAGE_VERSION,
    cachedAt: Date.now(),
    data,
    dataVersion,
  };
  try {
    window.localStorage.setItem(key, JSON.stringify(payload));
  } catch (error) {
    console.warn('[offline-cache] failed to persist snapshot', { namespace, error });
  }
}

export function loadOfflineSnapshot<T>(
  namespace: string,
  tenantSlug: string,
  expectedDataVersion?: string | number,
): OfflineSnapshot<T> | null {
  if (!isBrowser) {
    return null;
  }
  const key = buildKey(namespace, tenantSlug);
  const raw = window.localStorage.getItem(key);
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as StoredSnapshot<T> | null;
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }
    if (parsed.version !== STORAGE_VERSION || typeof parsed.cachedAt !== 'number') {
      return null;
    }
    if (expectedDataVersion !== undefined && parsed.dataVersion !== expectedDataVersion) {
      return null;
    }
    return { cachedAt: parsed.cachedAt, data: parsed.data, dataVersion: parsed.dataVersion };
  } catch (error) {
    console.warn('[offline-cache] failed to read snapshot', { namespace, error });
    return null;
  }
}

export function clearOfflineSnapshot(namespace: string, tenantSlug: string): void {
  if (!isBrowser) {
    return;
  }
  const key = buildKey(namespace, tenantSlug);
  try {
    window.localStorage.removeItem(key);
  } catch (error) {
    console.warn('[offline-cache] failed to clear snapshot', { namespace, error });
  }
}
